import React, { ReactNode } from 'react'
import { RouteProps } from 'react-router-dom'
import User from 'remixicon-react/UserLineIcon'
import Building from 'remixicon-react/Building4LineIcon'
import Settings2FillIcon from 'remixicon-react/Settings2FillIcon'
import { Organizations } from './Organizations'
import { Profile } from '~/Views/Main/Profile'

import { Info } from '~/Views/Main/Organizations/Info'
import { Users } from '~/Views/Main/Organizations/Users'
import { Licenses } from '~/Views/Main/Organizations/Licenses'
import { Roles } from '~/Views/Main/Organizations/Roles'
import { ServicePrincipals } from '~/Views/Main/Organizations/ServicePrincipals'
import {
  ApiKeys,
  CreateApiKey,
  EditApiKey,
} from '~/Views/Main/Organizations/ApiKeys'
import {
  ServicePrincipalRoles,
  EditServicePrincipalRole,
} from '~/Views/Main/Organizations/ServicePrincipalRoles'
import { Devices } from './Admin/Devices/Devices'
import { Clients } from './Admin/Clients/Clients'
import { Admin } from './Admin'
import { AdminLicenses } from './Admin/Licenses'
import { AdminProducts, ProductDetail } from './Admin/Products'
import { AdminOrganizations } from './Admin/Organizations'
import { AdminClaims } from './Admin/Claims'
import { AdminRoles } from './Admin/Roles'
import { AdminUsers } from './Admin/Users'
import { Staffs } from '~/Views/Main/Organizations/OrganizationStaffs'
import { Groups } from '~/Views/Main/Organizations/OrganizationGroups'
import { RoleManagement } from '~/Views/Main/Organizations/OrganizationRoles'
import { UserRoles } from './Admin/UserRoles'

export const enum RootClaim {
  System = 'System',
  Admin = 'Admin',
  User = 'User',
}

export const enum OrgClaim {
  Member = 'Member',
  Admin = 'Admin',
  Superuser = 'SuperUser',
}

export const enum SystemClaim {
  System = 'System',
  User = 'User',
}

interface RouteObj {
  label: string
  icon?: ReactNode
  rootPermission: RootClaim[]
  orgPermission: OrgClaim[]
  routeProps: RouteProps
  documentTitle?: string
  // Routed via the Switch but not shown as its own tab in the horizontal Menu
  // (e.g. a sub-page reached by navigating from another tab, not directly).
  hideFromMenu?: boolean
}

export const Routes: RouteObj[] = [
  {
    label: 'sidebar.organizations',
    rootPermission: [RootClaim.System, RootClaim.Admin],
    icon: <Building />,
    orgPermission: [OrgClaim.Member, OrgClaim.Superuser, OrgClaim.Admin],
    routeProps: {
      path: '/Organizations',
      children: <Organizations />,
    },
    documentTitle: 'header.organizations',
  },
  {
    label: 'sidebar.myProfile',
    rootPermission: [RootClaim.System, RootClaim.Admin, RootClaim.User],
    orgPermission: [],
    icon: <User />,
    routeProps: {
      path: '/Profile',
      children: <Profile />,
    },
    documentTitle: 'header.myProfile',
  },
  {
    label: 'sidebar.systemAdmin',
    rootPermission: [RootClaim.System],
    orgPermission: [],
    icon: <Settings2FillIcon />,
    routeProps: {
      path: '/Admin',
      children: <Admin />,
    },
    documentTitle: 'header.systemAdmin',
  },
]

export const OrgRoutes: RouteObj[] = [
  {
    label: 'sidebar.info',
    rootPermission: [RootClaim.System, RootClaim.Admin],
    orgPermission: [OrgClaim.Member, OrgClaim.Superuser, OrgClaim.Admin],
    routeProps: {
      path: '',
      children: <Info />,
    },
    documentTitle: 'header.organizationInfo',
  },
  {
    label: 'sidebar.users',
    rootPermission: [RootClaim.System, RootClaim.Admin],
    orgPermission: [OrgClaim.Member, OrgClaim.Superuser, OrgClaim.Admin],
    routeProps: {
      path: '/Users',
      children: <Users />,
    },
    documentTitle: 'header.organizationUsers',
  },
  {
    label: 'sidebar.licenses',
    rootPermission: [RootClaim.System, RootClaim.Admin],
    orgPermission: [OrgClaim.Superuser, OrgClaim.Admin],
    routeProps: {
      path: '/Licenses',
      children: <Licenses />,
    },
    documentTitle: 'header.licenses',
  },
  {
    label: 'sidebar.roles',
    rootPermission: [RootClaim.System, RootClaim.Admin],
    orgPermission: [OrgClaim.Superuser, OrgClaim.Admin],
    hideFromMenu: true,
    routeProps: {
      path: '/Roles',
      children: <Roles />,
    },
    documentTitle: 'header.roles',
  },
  {
    label: 'sidebar.staffs',
    rootPermission: [RootClaim.System, RootClaim.Admin],
    orgPermission: [OrgClaim.Superuser, OrgClaim.Admin],
    routeProps: {
      path: '/Staffs',
      children: <Staffs />,
    },
    documentTitle: 'header.staffs',
  },
  {
    label: 'sidebar.groups',
    rootPermission: [RootClaim.System, RootClaim.Admin],
    orgPermission: [OrgClaim.Superuser, OrgClaim.Admin],
    routeProps: {
      path: '/Groups',
      children: <Groups />,
    },
    documentTitle: 'header.groups',
  },
  {
    label: 'sidebar.roleManagement',
    rootPermission: [RootClaim.System, RootClaim.Admin],
    orgPermission: [OrgClaim.Superuser, OrgClaim.Admin],
    routeProps: {
      path: '/RoleManagement',
      children: <RoleManagement />,
    },
    documentTitle: 'header.roleManagement',
  },
  // Service Principals / SP Roles / API Keys are Organization-owned: the API
  // scopes every one of them to a single Organization and only accepts an
  // Admin/SuperUser of that Organization (or the System role).
  {
    label: 'sidebar.servicePrincipals',
    rootPermission: [RootClaim.System],
    orgPermission: [OrgClaim.Superuser, OrgClaim.Admin],
    routeProps: {
      path: '/ServicePrincipals',
      children: <ServicePrincipals />,
    },
    documentTitle: 'header.servicePrincipals',
  },
  {
    label: 'sidebar.servicePrincipalRoles',
    rootPermission: [RootClaim.System],
    orgPermission: [OrgClaim.Superuser, OrgClaim.Admin],
    routeProps: {
      path: '/ServicePrincipalRoles',
      children: <ServicePrincipalRoles />,
    },
    documentTitle: 'header.servicePrincipalRoles',
  },
  {
    label: 'sidebar.servicePrincipalRolesEdit',
    rootPermission: [RootClaim.System],
    orgPermission: [OrgClaim.Superuser, OrgClaim.Admin],
    hideFromMenu: true,
    routeProps: {
      path: '/ServicePrincipalRoles/Edit',
      children: <EditServicePrincipalRole />,
    },
    documentTitle: 'header.servicePrincipalRoles',
  },
  {
    label: 'sidebar.apiKeys',
    rootPermission: [RootClaim.System],
    orgPermission: [OrgClaim.Superuser, OrgClaim.Admin],
    routeProps: {
      path: '/ApiKeys',
      children: <ApiKeys />,
    },
    documentTitle: 'header.apiKeys',
  },
  {
    label: 'sidebar.apiKeysNew',
    rootPermission: [RootClaim.System],
    orgPermission: [OrgClaim.Superuser, OrgClaim.Admin],
    hideFromMenu: true,
    routeProps: {
      path: '/ApiKeys/New',
      children: <CreateApiKey />,
    },
    documentTitle: 'header.apiKeys',
  },
  {
    label: 'sidebar.apiKeysEdit',
    rootPermission: [RootClaim.System],
    orgPermission: [OrgClaim.Superuser, OrgClaim.Admin],
    hideFromMenu: true,
    routeProps: {
      path: '/ApiKeys/Edit',
      children: <EditApiKey />,
    },
    documentTitle: 'header.apiKeys',
  },
]

export const SystemRoutes: RouteObj[] = [
  {
    label: 'sidebar.organizations',
    rootPermission: [RootClaim.System],
    orgPermission: [],
    routeProps: {
      path: '',
      children: <AdminOrganizations />,
    },
    documentTitle: 'header.systemAdmin',
  },
  {
    label: 'sidebar.roles',
    rootPermission: [RootClaim.System],
    orgPermission: [],
    routeProps: {
      path: '/AdminRoles',
      children: <AdminRoles />,
    },
    documentTitle: 'header.roles',
  },
  {
    label: 'sidebar.users',
    rootPermission: [RootClaim.System, RootClaim.Admin],
    orgPermission: [OrgClaim.Member, OrgClaim.Superuser, OrgClaim.Admin],
    routeProps: {
      path: '/AdminUsers',
      children: <AdminUsers />,
    },
    documentTitle: 'header.systemAdmin',
  },
  {
    label: 'sidebar.licenseTypes',
    rootPermission: [RootClaim.System],
    orgPermission: [],
    routeProps: {
      path: '/LicenseTypes',
      children: <AdminLicenses />,
    },
    documentTitle: 'header.systemAdmin',
  },
  {
    label: 'sidebar.claims',
    rootPermission: [RootClaim.System, RootClaim.Admin],
    orgPermission: [],
    routeProps: {
      path: '/Claims',
      children: <AdminClaims />,
    },
    documentTitle: 'header.systemAdmin',
  },
  {
    label: 'sidebar.products',
    rootPermission: [RootClaim.System],
    orgPermission: [],
    routeProps: {
      path: '/Products',
      children: <AdminProducts />,
    },
    documentTitle: 'header.systemAdmin',
  },
  {
    label: 'sidebar.products',
    rootPermission: [RootClaim.System],
    orgPermission: [],
    hideFromMenu: true,
    routeProps: {
      path: '/Products/Detail',
      children: <ProductDetail />,
    },
    documentTitle: 'header.systemAdmin',
  },
  {
    label: 'sidebar.devices',
    rootPermission: [RootClaim.System],
    orgPermission: [],
    routeProps: {
      path: '/Devices',
      children: <Devices />,
    },
    documentTitle: 'header.systemAdmin',
  },
  {
    label: 'sidebar.clients',
    rootPermission: [RootClaim.System],
    orgPermission: [],
    routeProps: {
      path: '/Clients',
      children: <Clients />,
    },
    documentTitle: 'header.systemAdmin',
  },
  {
    label: 'sidebar.userRoles',
    rootPermission: [RootClaim.System],
    orgPermission: [],
    routeProps: {
      path: '/UserRoles',
      children: <UserRoles />,
    },
    documentTitle: 'header.systemAdmin',
  },
]
